import type { SalesChecklistSection } from "@expert/contentful";

import { Accordion, Group, Stack, Text } from "@mantine/core";
import { type JSX, type ReactNode, useEffect } from "react";

import { useSalesContext } from "../../../../../sales-context/SalesContext";
import {
    type OfferChecklistKey,
    type OfferChecklistState,
    useOfferChecklistStore,
} from "../../../../../stores/useOfferChecklistStore";
import { getCompletableItems } from "../utils/getCompletableItems";
import { isSectionComplete } from "../utils/isSectionComplete";
import { CoverageCarousel } from "./carousels/CoverageCarousel";
import { RapportCarousel } from "./carousels/RapportCarousel";
import { StringExamplesCarousel } from "./carousels/StringExamplesCarousel";
import { ChecklistItem } from "./ChecklistItem";
import { CompletionBadge } from "./CompletionBadge";

const KNOWN_RENDER_TYPES = [
    "RAPPORT",
    "COVERAGE",
    "DISCOVERY_QUESTION",
    "ENGAGEMENT_QUESTION",
    "GENERIC_CAROUSEL",
] as const;

export function ChecklistSection({
    section,
    productSku,
    onSectionComplete,
    prependSlot,
    appendSlot,
    hiddenItemKeys,
}: {
    section: SalesChecklistSection;
    productSku: string;
    onSectionComplete: (title: string) => void;
    prependSlot?: ReactNode;
    appendSlot?: ReactNode;
    hiddenItemKeys?: string[];
}): JSX.Element | null {
    const { sessionId, logger } = useSalesContext();
    const data = useOfferChecklistStore((state) => state.getData(sessionId, productSku));
    const visibleItems = hiddenItemKeys?.length
        ? section.checklistItems.filter((item) => !hiddenItemKeys.includes(item.key))
        : section.checklistItems;
    // Items blanked by a cohort override (no phrase/subheading) render null and can never be checked,
    // so they must not count toward completion — otherwise the section stays permanently incomplete.
    // They still render (carousel included) via `visibleItems` in the map below. See getCompletableItems.
    const completableItems = getCompletableItems(visibleItems);
    const isCompleted = isSectionComplete(completableItems, data);

    useEffect(() => {
        if (!isCompleted) return;
        onSectionComplete(section.title);
    }, [isCompleted, onSectionComplete, section.title]);

    useEffect(() => {
        const trackedKeys = ["RAPPORT", "COVERAGE", "DISCOVERY_QUESTION", "ENGAGEMENT_QUESTION"] as const;
        for (const item of visibleItems) {
            const hasUnknownAdditionalRenderType =
                item.additionalRenderType != null &&
                !(KNOWN_RENDER_TYPES as readonly string[]).includes(item.additionalRenderType);
            if (hasUnknownAdditionalRenderType) {
                logger.debug(
                    { key: item.key, additionalRenderType: item.additionalRenderType },
                    "[ChecklistSection] unknown additionalRenderType, falling back to GENERIC_CAROUSEL",
                );
            }
            // Remove this once all Contentful Entries are migrated to use additionalRenderType
            if (
                trackedKeys.includes(item.key as (typeof trackedKeys)[number]) &&
                item.additionalRenderType !== item.key
            ) {
                logger.debug(
                    { key: item.key, additionalRenderType: item.additionalRenderType },
                    "[ChecklistSection] item.key has no matching additionalRenderType",
                );
            }
        }
    }, [visibleItems, logger]);

    return (
        <>
            <Accordion.Item key={section.title} value={section.title}>
                <Accordion.Control styles={{ control: { opacity: 1 } }}>
                    <Group justify="space-between" pr={"sm"}>
                        <Text size="xs" c="dark.1" fw="var(--mantine-font-weight-heavy)">
                            {section.title}
                        </Text>
                        <CompletionBadge
                            totalSteps={completableItems.length}
                            completedSteps={getCompletedSteps(completableItems, data)}
                        />
                    </Group>
                </Accordion.Control>
                <Accordion.Panel>
                    <Stack gap="xs">
                        {prependSlot}
                        {visibleItems.map((item) => {
                            const rawRenderType = item.additionalRenderType ?? item.key;
                            // Only remap to GENERIC_CAROUSEL when Contentful explicitly set additionalRenderType
                            // to a value this UI doesn't recognize (e.g. a new render type shipped ahead of a UI
                            // deploy). If additionalRenderType is missing and item.key isn't a render-type name
                            // (phrase-only items like TRANSITION_STATEMENT), leave the value alone so no example
                            // area renders below the item text.
                            const hasUnknownAdditionalRenderType =
                                item.additionalRenderType != null &&
                                !(KNOWN_RENDER_TYPES as readonly string[]).includes(item.additionalRenderType);
                            const renderType = hasUnknownAdditionalRenderType ? "GENERIC_CAROUSEL" : rawRenderType;
                            return (
                                <Stack gap={4} key={item.phrase}>
                                    <ChecklistItem item={item} productSku={productSku} />
                                    {renderType === "RAPPORT" && (
                                        <RapportCarousel defaultExample={item.defaultExample} />
                                    )}
                                    {renderType === "COVERAGE" && (
                                        <CoverageCarousel
                                            defaultExample={item.defaultExample}
                                            staticExamples={item.examples}
                                        />
                                    )}
                                    {(renderType === "DISCOVERY_QUESTION" ||
                                        renderType === "ENGAGEMENT_QUESTION" ||
                                        renderType === "GENERIC_CAROUSEL") && (
                                        <StringExamplesCarousel examples={item.examples ?? []} />
                                    )}
                                </Stack>
                            );
                        })}
                        {appendSlot}
                    </Stack>
                </Accordion.Panel>
            </Accordion.Item>
        </>
    );
}

const getCompletedSteps = (items: SalesChecklistSection["checklistItems"], data: OfferChecklistState["data"]) => {
    return items.filter((item) => data[item.key as OfferChecklistKey]).length;
};
